import moment from 'moment-timezone';

// Streak-shield (盾, "tate") tuning:
// - Earn 1 shield for every SHIELD_EARN_DAYS consecutive study days.
// - Hold at most SHIELD_CAP shields at once.
// A missed day auto-spends a shield to keep the streak alive (and resets the
// earn counter); with no shield left, the streak breaks.
export const SHIELD_EARN_DAYS = 7;
export const SHIELD_CAP = 3;

// Simulate at most ~5 years so a corrupt/huge activity log can't spin forever.
const SAFETY_CAP = 1826;

/**
 * Computes the user's current daily streak from the three append-only activity
 * logs, using an earned "streak shield" model:
 *
 *   1. Shields (盾, "tate") — every SHIELD_EARN_DAYS consecutive study days earn 1
 *      shield (held up to SHIELD_CAP). A missed day auto-spends a shield to keep the
 *      streak alive and resets the earn counter; with 0 shields the streak breaks.
 *      Days a shield protected are reported as `protectedDates` for the heatmap.
 *   2. Vacation — days the user spent in vacation mode are neutral: they neither
 *      break nor extend the streak, and don't earn or spend shields. Covers both
 *      the active vacation (open-ended at `vacationStartedAt`) and completed ones
 *      recorded in `vacationPeriods`.
 *
 * A day "counts" when the summed count across learned_today + reviewed_words_today
 * + reviewed_sentences_today is greater than zero.
 *
 * Implementation: a single forward pass from the user's first recorded activity
 * (clamped to SAFETY_CAP days back) up to today. A real break resets streak/
 * shields/counter to zero, so by the time the pass reaches today the result
 * reflects only the current streak — no need to locate where it started.
 *
 * @param {Object}  params
 * @param {Object}  params.learnedTodayData         { 'YYYY-MM-DD': count }
 * @param {Object}  params.reviewedWordsData        { 'YYYY-MM-DD': count }
 * @param {Object}  params.reviewedSentencesData    { 'YYYY-MM-DD': count }
 * @param {string}  [params.userTimezone='UTC']
 * @param {*}       [params.vacationStartedAt=null] Firestore Timestamp | Date | null — active vacation
 * @param {Array}   [params.vacationPeriods=[]]     [{ start, end }] of completed vacations (Timestamp | Date | ISO)
 * @returns {{ streak: number, shields: number, runLength: number, protectedDaysUsed: number, protectedDates: Set<string> }}
 *   runLength = current consecutive study-day count toward the next shield.
 */
export function computeStreak({
  learnedTodayData,
  reviewedWordsData,
  reviewedSentencesData,
  userTimezone = 'UTC',
  vacationStartedAt = null,
  vacationPeriods = [],
}) {
  const hasActivity = (key) =>
    (learnedTodayData?.[key] || 0) +
      (reviewedWordsData?.[key] || 0) +
      (reviewedSentencesData?.[key] || 0) >
    0;

  const isVacationDay = buildVacationPredicate({ vacationStartedAt, vacationPeriods, userTimezone });

  const todayKey = moment().tz(userTimezone).format('YYYY-MM-DD');

  // Start from the earliest recorded activity (clamped to SAFETY_CAP days back).
  const allKeys = [
    ...Object.keys(learnedTodayData || {}),
    ...Object.keys(reviewedWordsData || {}),
    ...Object.keys(reviewedSentencesData || {}),
  ];
  if (allKeys.length === 0) {
    return { streak: 0, shields: 0, runLength: 0, protectedDaysUsed: 0, protectedDates: new Set() };
  }
  const capStartKey = moment().tz(userTimezone).subtract(SAFETY_CAP, 'days').format('YYYY-MM-DD');
  let startKey = allKeys.reduce((min, k) => (k < min ? k : min), allKeys[0]);
  if (startKey < capStartKey) startKey = capStartKey;

  let streak = 0;
  let runLength = 0; // consecutive study days toward the next shield
  let shields = 0;
  let protectedDays = []; // days a shield protected the streak (current streak only)

  const cursor = moment.tz(startKey, 'YYYY-MM-DD', userTimezone).startOf('day');
  const today = moment.tz(todayKey, 'YYYY-MM-DD', userTimezone).startOf('day');

  for (let guard = 0; cursor.isSameOrBefore(today) && guard <= SAFETY_CAP + 1; guard++) {
    const key = cursor.format('YYYY-MM-DD');

    if (hasActivity(key)) {
      streak += 1;
      runLength += 1;
      if (runLength % SHIELD_EARN_DAYS === 0) {
        shields = Math.min(shields + 1, SHIELD_CAP); // earned a shield
      }
    } else if (isVacationDay(key)) {
      // Neutral — neither break nor earn/spend.
    } else if (key === todayKey) {
      // Today has no activity yet: it's in progress, not a miss. Leave the streak
      // as-is (don't break, don't spend a shield).
    } else if (shields > 0) {
      shields -= 1; // auto-spend to survive the miss
      runLength = 0; // must rebuild SHIELD_EARN_DAYS in a row for the next shield
      protectedDays.push(key); // streak unchanged
    } else {
      // No shield to cover the miss — streak breaks; reset everything.
      streak = 0;
      runLength = 0;
      shields = 0;
      protectedDays = [];
    }

    cursor.add(1, 'day');
  }

  return {
    streak,
    shields,
    runLength,
    protectedDaysUsed: protectedDays.length,
    protectedDates: new Set(protectedDays),
  };
}

/**
 * Would being active today earn the user a new 盾? Compares the shield count with
 * today's activity as-is against today forced active. A session calls this up front
 * (before its writes) to decide whether the end screen should show the "盾 earned"
 * banner — so the end screen reads a boolean instead of recomputing from metadata
 * that may still be propagating. Returns false if today is already active (the 盾
 * was earned by an earlier session today, not this one).
 */
export function willEarnShieldToday({
  learnedTodayData,
  reviewedWordsData,
  reviewedSentencesData,
  userTimezone = 'UTC',
  vacationStartedAt = null,
  vacationPeriods = [],
}) {
  const params = { learnedTodayData, reviewedWordsData, reviewedSentencesData, userTimezone, vacationStartedAt, vacationPeriods };
  const before = computeStreak(params).shields;
  const todayKey = moment().tz(userTimezone).format('YYYY-MM-DD');
  const after = computeStreak({
    ...params,
    learnedTodayData: { ...(learnedTodayData || {}), [todayKey]: (learnedTodayData?.[todayKey] || 0) + 1 },
  }).shields;
  return after > before;
}

// Normalize any vacation timestamp/Date/ISO string to a YYYY-MM-DD key in the
// user's timezone. String comparison is valid for the fixed-width format.
function toDayKey(value, userTimezone) {
  const d = value && typeof value.toDate === 'function' ? value.toDate() : value;
  return moment(d).tz(userTimezone).format('YYYY-MM-DD');
}

// Builds a predicate `(key) => boolean` that reports whether a YYYY-MM-DD day
// fell within any vacation: the open-ended active vacation (>= its start) or any
// completed { start, end } period from vacation_history.
function buildVacationPredicate({ vacationStartedAt = null, vacationPeriods = [], userTimezone = 'UTC' }) {
  const closedRanges = (Array.isArray(vacationPeriods) ? vacationPeriods : [])
    .filter((p) => p && p.start && p.end)
    .map((p) => ({ startKey: toDayKey(p.start, userTimezone), endKey: toDayKey(p.end, userTimezone) }));

  const activeStartKey = vacationStartedAt ? toDayKey(vacationStartedAt, userTimezone) : null;

  return (key) => {
    if (activeStartKey !== null && key >= activeStartKey) return true;
    return closedRanges.some((r) => key >= r.startKey && key <= r.endKey);
  };
}

/**
 * Returns the set of YYYY-MM-DD day keys that fell within a vacation, over the
 * inclusive [start, end] range. Decoupled from the streak walk so the heatmap can
 * mark vacation days even in months where the streak later broke.
 *
 * @param {Object} params
 * @param {*}      [params.vacationStartedAt=null]
 * @param {Array}  [params.vacationPeriods=[]]
 * @param {string} [params.userTimezone='UTC']
 * @param {string} params.start  start day key 'YYYY-MM-DD' (inclusive)
 * @param {string} params.end    end day key 'YYYY-MM-DD' (inclusive)
 * @returns {Set<string>}
 */
export function getVacationDates({ vacationStartedAt = null, vacationPeriods = [], userTimezone = 'UTC', start, end }) {
  const result = new Set();
  if (!start || !end) return result;

  const isVacationDay = buildVacationPredicate({ vacationStartedAt, vacationPeriods, userTimezone });
  const cursor = moment.tz(start, 'YYYY-MM-DD', userTimezone).startOf('day');
  const last = moment.tz(end, 'YYYY-MM-DD', userTimezone).startOf('day');

  for (let guard = 0; cursor.isSameOrBefore(last) && guard < 4000; guard++) {
    const key = cursor.format('YYYY-MM-DD');
    if (isVacationDay(key)) result.add(key);
    cursor.add(1, 'day');
  }
  return result;
}
